import React, { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { toast } from "react-toastify";
import axios from "axios";

const UploadAsBuiltModal = ({ show, onHide, ticketInfo }) => {
  const [formData, setFormData] = useState({
    files: [],
    lat: "",
    lng: "",
    radius: 100,
    ticketNumber: "",
    fileName: "",
  });
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({}); // Track progress for each file
  const [uploadResults, setUploadResults] = useState({}); // Track success/failure for each file

  // Prefill form when modal opens or ticketInfo changes
  useEffect(() => {
    if (show && ticketInfo?.detail) {
      setFormData({
        files: [],
        lat: ticketInfo.detail.locationLat || "",
        lng: ticketInfo.detail.locationLng || "",
        radius: 100,
        ticketNumber: ticketInfo.detail.ticketNo || "",
        fileName: "",
      });
      setUploadProgress({});
      setUploadResults({});
    }
  }, [show, ticketInfo]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Validation handler for latitude and longitude (allows numbers, minus sign, and decimal point)
  const handleLatLngChange = (e) => {
    const { name, value } = e.target;
    // Allow: numbers, minus sign, decimal point, and empty string
    const validPattern = /^-?\d*\.?\d*$/;
    if (value === "" || validPattern.test(value)) {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Validation handler for radius (allows numbers and decimal point only, no minus sign)
  const handleRadiusChange = (e) => {
    const { name, value } = e.target;
    // Allow: numbers and decimal point only (no minus sign)
    const validPattern = /^\d*\.?\d*$/;
    if (value === "" || validPattern.test(value)) {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Prevent invalid key presses for lat/lng
  const handleLatLngKeyDown = (e) => {
    const { key } = e;
    // Allow: numbers, minus, decimal point, backspace, delete, tab, arrow keys, etc.
    const allowedKeys = [
      "Backspace",
      "Delete",
      "Tab",
      "Escape",
      "Enter",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Home",
      "End",
    ];

    if (allowedKeys.includes(key)) {
      return;
    }

    // Allow Ctrl/Cmd + A, C, V, X
    if (
      (e.ctrlKey || e.metaKey) &&
      ["a", "c", "v", "x"].includes(key.toLowerCase())
    ) {
      return;
    }

    // Allow numbers
    if (key >= "0" && key <= "9") {
      return;
    }

    // Allow minus sign (only at the start)
    if (key === "-" && e.target.selectionStart === 0) {
      return;
    }

    // Allow decimal point (only one)
    if (key === "." && !e.target.value.includes(".")) {
      return;
    }

    // Prevent all other keys
    e.preventDefault();
  };

  // Prevent invalid key presses for radius
  const handleRadiusKeyDown = (e) => {
    const { key } = e;
    // Allow: numbers, decimal point, backspace, delete, tab, arrow keys, etc.
    const allowedKeys = [
      "Backspace",
      "Delete",
      "Tab",
      "Escape",
      "Enter",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Home",
      "End",
    ];

    if (allowedKeys.includes(key)) {
      return;
    }

    // Allow Ctrl/Cmd + A, C, V, X
    if (
      (e.ctrlKey || e.metaKey) &&
      ["a", "c", "v", "x"].includes(key.toLowerCase())
    ) {
      return;
    }

    // Allow numbers
    if (key >= "0" && key <= "9") {
      return;
    }

    // Allow decimal point (only one)
    if (key === "." && !e.target.value.includes(".")) {
      return;
    }

    // Prevent all other keys (including minus sign)
    e.preventDefault();
  };

  // Handle paste events for lat/lng - filter invalid characters
  const handleLatLngPaste = (e) => {
    e.preventDefault();
    const pastedText = (e.clipboardData || window.clipboardData).getData(
      "text"
    );
    // Filter to only allow numbers, minus sign, and decimal point
    const filtered = pastedText.replace(/[^0-9.-]/g, "");
    // Ensure only one decimal point and minus at start
    const parts = filtered.split(".");
    let cleaned =
      parts.length > 2 ? parts[0] + "." + parts.slice(1).join("") : filtered;
    if (cleaned.includes("-") && cleaned.indexOf("-") !== 0) {
      cleaned = cleaned.replace(/-/g, "");
      if (cleaned && !cleaned.startsWith("-")) {
        cleaned = "-" + cleaned;
      }
    }
    const target = e.target;
    const start = target.selectionStart;
    const end = target.selectionEnd;
    const newValue =
      formData[target.name].substring(0, start) +
      cleaned +
      formData[target.name].substring(end);

    // Validate the new value
    const validPattern = /^-?\d*\.?\d*$/;
    if (validPattern.test(newValue)) {
      setFormData((prev) => ({
        ...prev,
        [target.name]: newValue,
      }));
    }
  };

  // Handle paste events for radius - filter invalid characters
  const handleRadiusPaste = (e) => {
    e.preventDefault();
    const pastedText = (e.clipboardData || window.clipboardData).getData(
      "text"
    );
    // Filter to only allow numbers and decimal point (no minus sign)
    const filtered = pastedText.replace(/[^0-9.]/g, "");
    // Ensure only one decimal point
    const parts = filtered.split(".");
    const cleaned =
      parts.length > 2 ? parts[0] + "." + parts.slice(1).join("") : filtered;
    const target = e.target;
    const start = target.selectionStart;
    const end = target.selectionEnd;
    const newValue =
      formData[target.name].substring(0, start) +
      cleaned +
      formData[target.name].substring(end);

    // Validate the new value
    const validPattern = /^\d*\.?\d*$/;
    if (validPattern.test(newValue)) {
      setFormData((prev) => ({
        ...prev,
        [target.name]: newValue,
      }));
    }
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length === 0) {
      return;
    }

    // Validate file types
    const allowedTypes = [
      "application/pdf",
      "image/tiff",
      "image/png",
      "image/jpeg",
      "image/jpg",
    ];
    const allowedExtensions = [
      ".pdf",
      ".tif",
      ".tiff",
      ".png",
      ".jpg",
      ".jpeg",
    ];

    const validFiles = [];
    const invalidFiles = [];

    selectedFiles.forEach((file) => {
      const fileExtension = file.name
        .substring(file.name.lastIndexOf("."))
        .toLowerCase();

      if (
        allowedTypes.includes(file.type) ||
        allowedExtensions.includes(fileExtension)
      ) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file.name);
      }
    });

    if (invalidFiles.length > 0) {
      toast.error(
        `Invalid file type(s): ${invalidFiles.join(
          ", "
        )}. Please upload PDF, TIF, PNG, or JPEG files only.`
      );
    }

    if (validFiles.length > 0) {
      setFormData((prev) => ({
        ...prev,
        files: validFiles,
      }));
      setUploadProgress({});
      setUploadResults({});
    } else {
      e.target.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.files || formData.files.length === 0) {
      toast.error("Please select at least one file to upload");
      return;
    }

    if (!formData.lat || !formData.lng) {
      toast.error("Latitude and Longitude are required");
      return;
    }

    if (!formData.radius || formData.radius <= 0) {
      toast.error("Radius must be greater than 0");
      return;
    }

    setIsUploading(true);
    setUploadProgress({});
    setUploadResults({});

    // Get current user from localStorage
    const user = JSON.parse(localStorage.getItem("user"));
    const userId = user?.id || "";
    const bearer_token = localStorage.getItem("accessToken");
    const apiUrl = `${process.env.REACT_APP_BACKEND_URL}/custom-asbuilt`;

    const uploadPromises = formData.files.map(async (file, index) => {
      // Use provided fileName as base, or original file name if not provided
      const fileName = formData.fileName.trim()
        ? formData.files.length > 1
          ? `${formData.fileName} (${index + 1})`
          : formData.fileName
        : file.name;

      try {
        // Update progress
        setUploadProgress((prev) => ({
          ...prev,
          [file.name]: "uploading",
        }));

        // Create metadata with uploadedBy
        const metadata = {
          uploadedBy: userId,
        };

        // Create FormData for this file
        const uploadFormData = new FormData();
        uploadFormData.append("file", file);
        uploadFormData.append("lat", parseFloat(formData.lat));
        uploadFormData.append("lng", parseFloat(formData.lng));
        uploadFormData.append("radius", parseFloat(formData.radius));
        uploadFormData.append("ticketNumber", formData?.ticketNumber || "");
        uploadFormData.append("fileName", fileName);
        uploadFormData.append("metadata", JSON.stringify(metadata));

        // Upload file
        const response = await axios.post(apiUrl, uploadFormData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${bearer_token}`,
          },
        });

        // Mark as success
        setUploadProgress((prev) => ({
          ...prev,
          [file.name]: "success",
        }));
        setUploadResults((prev) => ({
          ...prev,
          [file.name]: { success: true, message: "Uploaded successfully" },
        }));

        return { file: file.name, success: true, response: response.data };
      } catch (error) {
        console.error(`Upload error for ${file.name}:`, error);
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Failed to upload file";

        // Mark as failed
        setUploadProgress((prev) => ({
          ...prev,
          [file.name]: "error",
        }));
        setUploadResults((prev) => ({
          ...prev,
          [file.name]: { success: false, message: errorMessage },
        }));

        return { file: file.name, success: false, error: errorMessage };
      }
    });

    // Wait for all uploads to complete
    const results = await Promise.all(uploadPromises);

    // Count successes and failures
    const successCount = results.filter((r) => r.success).length;
    const failureCount = results.filter((r) => !r.success).length;

    // Show summary toast
    if (failureCount === 0) {
      toast.success(
        `All ${successCount} file${
          successCount > 1 ? "s" : ""
        } uploaded successfully!`
      );
      // Reset form and close modal after a short delay
      setTimeout(() => {
        setFormData({
          files: [],
          lat: ticketInfo?.detail?.locationLat || "",
          lng: ticketInfo?.detail?.locationLng || "",
          radius: 100,
          ticketNumber: ticketInfo?.detail?.ticketNo || "",
          fileName: "",
        });
        setUploadProgress({});
        setUploadResults({});
        onHide();
      }, 1500);
    } else if (successCount > 0) {
      toast.warning(
        `${successCount} file${
          successCount > 1 ? "s" : ""
        } uploaded successfully, ${failureCount} failed.`
      );
    } else {
      toast.error(
        `All ${failureCount} file${
          failureCount > 1 ? "s" : ""
        } failed to upload.`
      );
    }

    setIsUploading(false);
  };

  const handleClose = () => {
    if (!isUploading) {
      setFormData({
        files: [],
        lat: ticketInfo?.detail?.locationLat || "",
        lng: ticketInfo?.detail?.locationLng || "",
        radius: 100,
        ticketNumber: ticketInfo?.detail?.ticketNo || "",
        fileName: "",
      });
      setUploadProgress({});
      setUploadResults({});
      onHide();
    }
  };

  const removeFile = (index) => {
    if (!isUploading) {
      setFormData((prev) => ({
        ...prev,
        files: prev.files.filter((_, i) => i !== index),
      }));
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Upload Custom AsBuilt File</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {/* File Upload */}
          <Form.Group className="mb-3">
            <Form.Label>
              Files <span style={{ color: "red" }}>*</span>
            </Form.Label>
            <Form.Control
              type="file"
              accept=".pdf,.tif,.tiff,.png,.jpg,.jpeg,application/pdf,image/tiff,image/png,image/jpeg"
              onChange={handleFileChange}
              disabled={isUploading}
              multiple
              required
            />
            <Form.Text className="text-muted">
              Accepted formats: PDF, TIF, PNG, JPEG. You can select multiple
              files.
            </Form.Text>
            {formData.files.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    marginBottom: 8,
                    color: "#495057",
                  }}
                >
                  Selected Files ({formData.files.length}):
                </div>
                <div
                  style={{
                    maxHeight: 200,
                    overflowY: "auto",
                    border: "1px solid #dee2e6",
                    borderRadius: 4,
                    padding: 8,
                    backgroundColor: "#f8f9fa",
                  }}
                >
                  {formData.files.map((file, index) => {
                    const status = uploadProgress[file.name];
                    const result = uploadResults[file.name];
                    return (
                      <div
                        key={index}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "6px 8px",
                          marginBottom: 4,
                          backgroundColor: "white",
                          borderRadius: 4,
                          border: "1px solid #dee2e6",
                        }}
                      >
                        <div style={{ flex: 1, fontSize: 12 }}>
                          <div style={{ fontWeight: 500, color: "#212529" }}>
                            {file.name}
                          </div>
                          <div style={{ fontSize: 11, color: "#6c757d" }}>
                            {(file.size / 1024 / 1024).toFixed(2)} MB
                          </div>
                          {status === "uploading" && (
                            <div
                              style={{
                                fontSize: 11,
                                color: "#007bff",
                                marginTop: 4,
                              }}
                            >
                              ⏳ Uploading...
                            </div>
                          )}
                          {status === "success" && (
                            <div
                              style={{
                                fontSize: 11,
                                color: "#28a745",
                                marginTop: 4,
                              }}
                            >
                              ✅ {result?.message || "Uploaded successfully"}
                            </div>
                          )}
                          {status === "error" && (
                            <div
                              style={{
                                fontSize: 11,
                                color: "#dc3545",
                                marginTop: 4,
                              }}
                            >
                              ❌ {result?.message || "Upload failed"}
                            </div>
                          )}
                        </div>
                        {!isUploading && (
                          <button
                            type="button"
                            onClick={() => removeFile(index)}
                            style={{
                              background: "none",
                              border: "none",
                              color: "#dc3545",
                              cursor: "pointer",
                              fontSize: 18,
                              padding: "0 8px",
                              lineHeight: 1,
                            }}
                            title="Remove file"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </Form.Group>

          {/* Ticket Number */}
          <Form.Group className="mb-3">
            <Form.Label>Ticket Number</Form.Label>
            <Form.Control
              type="text"
              name="ticketNumber"
              value={formData.ticketNumber}
              onChange={handleInputChange}
              disabled={isUploading}
            />
          </Form.Group>

          {/* Latitude and Longitude Row */}
          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>
                  Latitude <span style={{ color: "red" }}>*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="lat"
                  value={formData.lat}
                  onChange={handleLatLngChange}
                  onKeyDown={handleLatLngKeyDown}
                  onPaste={handleLatLngPaste}
                  step="any"
                  disabled={isUploading}
                  required
                  placeholder="e.g., 43.4643 or -80.5204"
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>
                  Longitude <span style={{ color: "red" }}>*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="lng"
                  value={formData.lng}
                  onChange={handleLatLngChange}
                  onKeyDown={handleLatLngKeyDown}
                  onPaste={handleLatLngPaste}
                  step="any"
                  disabled={isUploading}
                  required
                  placeholder="e.g., 43.4643 or -80.5204"
                />
              </Form.Group>
            </div>
          </div>

          {/* Radius */}
          <Form.Group className="mb-3">
            <Form.Label>
              Radius (meters) <span style={{ color: "red" }}>*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="radius"
              value={formData.radius}
              onChange={handleRadiusChange}
              onKeyDown={handleRadiusKeyDown}
              onPaste={handleRadiusPaste}
              disabled={isUploading}
              required
              placeholder="e.g., 100"
            />
            <Form.Text className="text-muted">
              The radius in meters around the coordinates where this file will
              be visible
            </Form.Text>
          </Form.Group>

          {/* File Name */}
          <Form.Group className="mb-3">
            <Form.Label>File Name (Optional)</Form.Label>
            <Form.Control
              type="text"
              name="fileName"
              value={formData.fileName}
              onChange={handleInputChange}
              disabled={isUploading}
              placeholder="Base name for files (e.g., 'AsBuilt-2024')"
            />
            <Form.Text className="text-muted">
              {formData.files.length > 1
                ? formData.fileName.trim()
                  ? `Multiple files will be named: "${formData.fileName} (1)", "${formData.fileName} (2)", etc.`
                  : "If left empty, original file names will be used. Or provide a base name to rename all files."
                : "Custom display name for the file. If left empty, the original file name will be used."}
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isUploading}
          >
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={isUploading}>
            {isUploading ? "Uploading..." : "Upload"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default UploadAsBuiltModal;
